import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Modal, Tooltip } from "flowbite-react";
import { flatMap, has } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import api from "../../apis/axiosInterceptor";
import allImgPaths from "../../assets";
import { IconButton } from "../../components";
import Layout, { Breadcrumb, Container } from "../../components/layout";
import Table from "../../components/table";
import { useAppState } from "../../context";
import { useDebounce } from "../../hooks/useDebounce";
import useOutsideClick from "../../hooks/useOutsideClick";
import ImportCsv from "./../../components/common/ImportCsv";

type Employers = {
  _id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
};
const PAGE_LIMIT = 10;
let flagOfIndex = 1;
const Employer = () => {
  const navigate = useNavigate();
  const {
    getEmployers,
    employersData,
    importEmployer,
    isLoading,
    totalPageCount,
    totalData,
    updateState,
  } = useAppState("employers");
  const PAGE_LIMIT_OPTIONS = [10, 25, 50, 100];
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_LIMIT);
  const actionsRef = useRef(null);
  const [openImportEmployerModal, setOpenImportEmployerModal] = useState(false);
  const [employerData, setEmployerData] = useState("");
  const [isExportEmployer, setIsExportEmployer] = useState(0);
  const [open, setOpen] = useState(false);
  const [curUserData, setCurUserData] = useState<any>();
  const [fromSearch, setFromSearch] = useState(false);
  // Function to handle rows per page change

  const cPage = currentPage;

  const startIndex = (cPage - 1) * rowsPerPage + 1;
  const endIndex = startIndex - 1 + employersData.length;
  // const endIndex = 10;
  const totalRecords = totalData;

  const editEmployerHandler = (id: number) => {
    navigate(`/employer/edit/${id}`);
  };

  useOutsideClick(actionsRef, () => {
    isOpen && setIsOpen((prev) => !prev);
  });
  const getEmployeesDetailById = async (userId: string) => {
    try {
      const result: any = await api.get(
        `/api/v1/users/findOne?userId=${userId}`,
      );

      if (result.data.status) {
        setCurUserData(result.data.data);

        return true;
      } else {
        const errorStr = has(result.data, "errors")
          ? Object(flatMap(result.data))[1].msg
          : result.data.message;
        toast.error(errorStr);
        // setState({ ...state, error: errorStr });
        setCurUserData({ error: errorStr });
        return false;
      }
    } catch (error: any) {
      toast.error(error.message);
      // setState({ ...state, error: error.message });
      setCurUserData({ error: error.message });
      return false;
    }
  };
  /* Table Section Start */
  const columnHelper = createColumnHelper<Employers>();
  const defaultColumns = [
    columnHelper.accessor(
      (row, index: number) => (flagOfIndex - 1) * rowsPerPage + index + 1,
      {
        id: "no",
        header: () => <span className="">No.</span>,
        cell: (info) => (
          <span className="text-[#1B1C1E]">{info.getValue()}</span>
        ),
      },
    ),
    columnHelper.accessor((row) => row.firstName, {
      id: "firstName",
      header: () => <span className="">First Name</span>,
      cell: (info) => <span className="text-[#1B1C1E]">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.lastName, {
      id: "lastName",
      header: () => <span className="">Last Name</span>,
      cell: (info) => <span className="text-[#1B1C1E]">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.email, {
      id: "email",
      header: () => <span className="">Email Id</span>,
      cell: (info) => <span className="text-[#1B1C1E]">{info.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.createdAt, {
      id: "createdAt",
      header: () => <span className="">Date</span>,
      cell: (info) => (
        <span className="capitalize text-[#1B1C1E]">
          {moment(info.getValue()).format("DD/MM/YYYY")}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row._id, {
      id: "status_id",
      header: () => <span className="">Action</span>,
      cell: (info) => (
        <div className="flex gap-2">
          <Tooltip content="Edit" placement="bottom">
            <IconButton
              icon={allImgPaths.actionEdit}
              type={"button"}
              className="bg-[#0EA5E9]"
              onClick={() => {
                editEmployerHandler(info.getValue());
              }}
            />
          </Tooltip>
          <Tooltip content="Preview" placement="bottom">
            <IconButton
              icon={allImgPaths.eyeOpenWhite}
              className={`bg-[#808b9a]`}
              onClick={() => {
                handleUserById(info.getValue());
              }}
            />
          </Tooltip>
        </div>
      ),
    }),
  ];
  const [columns, setColumns] = useState<typeof defaultColumns>(() => [
    ...defaultColumns,
  ]);
  const table = useReactTable({
    data: employersData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: rowsPerPage,
      },
    },
  });
  /* Table Section End */
  const debouncedSearch: string | number = useDebounce(search, 700);

  const addNewEmployerHandler = () => {
    updateState({ curEmployerEditData: {} });
    navigate("/employer/create");
  };

  //Index Purpose Flag
  const handleRowsPerPageChange = (event: any) => {
    setRowsPerPage(parseInt(event.target.value));
    setCurrentPage(1); // Reset to the first page when changing rows per page
    table.setPageSize(parseInt(event.target.value));
  };
  /* New code start */
  useEffect(() => {
    flagOfIndex = currentPage;
  }, [currentPage]);
  useEffect(() => {
    setColumns([...defaultColumns]);
  }, [rowsPerPage]);
  /* New code End */
  useEffect(() => {
    getEmployers(currentPage, rowsPerPage, debouncedSearch);
  }, [currentPage, rowsPerPage, debouncedSearch]);

  const importEmployerModal = () => {
    setOpenImportEmployerModal(true);
  };

  const submitCsvImportHandler = (data: any) => {
    if (importEmployer({ files: data.file })) {
      setOpenImportEmployerModal(false);
    }
  };

  const closeCsvModalHandler = () => {
    setOpenImportEmployerModal(false);
  };

  const employerExport = async () => {
    try {
      const result = await api.get(
        `/api/v1/users/employerExport?role=Employer`,
      );
      setEmployerData(result.data.data);
      setIsExportEmployer(result.data.employerCount);
    } catch (error: any) {
      if (
        error.response &&
        error.response.status !== 402 &&
        error.response.status !== 404
      ) {
        toast.error("Error while fetching teacher export credentials");
      }
    }
    isOpen && setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    employerExport();
  }, []);
  const handleUserById = async (userId: any) => {
    const res = await getEmployeesDetailById(userId);

    if (res) {
      setOpen(true);
    }
  };
  useEffect(() => {
    if (!fromSearch) {
      setCurrentPage(currentPage);
    } else {
      setCurrentPage(1);
    }
  });
  return (
    <Layout>
      <Modal
        show={openImportEmployerModal}
        onClose={() => setOpenImportEmployerModal(false)}
      >
        <Modal.Header className="[&_.ml-auto]:!hidden">
          Import Employer
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <ImportCsv
              description={
                <div>
                  <p>
                    Note : Allowed file type ( .csv )
                    <p>
                      Field and data must be in format given an example file:{" "}
                      <CSVLink
                        className="underline text-blue-500"
                        filename={"example.csv"}
                        data={[
                          {
                            Firstname: "",
                            Lastname: "",
                            Email: "",
                            Phone: "",
                            CompanyName: "",
                            CompanyUrl: "",
                            CompanySize: "",
                            CompanyFounded: "",
                            CompanyOverview: "",
                            CompanyDiversity: "",
                            CompanyScore: "",
                          },
                        ]}
                      >
                        example.csv
                      </CSVLink>
                    </p>
                  </p>
                </div>
              }
              onSubmit={submitCsvImportHandler}
              closeModal={closeCsvModalHandler}
            ></ImportCsv>
          </div>
        </Modal.Body>
      </Modal>
      <Breadcrumb title="Employer Listing" />
      <Container>
        <div className="">
          <div ref={actionsRef} className="flex justify-between gap-5 w-full">
            <div>
              <div className="relative h-full">
                <input
                  className="rounded-[5px] !h-9 bg-[#F4F4F4] outline-none focus:!shadow-none border-[1px] outline-0 text-sm"
                  type="text"
                  value={search}
                  placeholder="Search Employer"
                  onChange={(event) => {
                    setFromSearch(true);
                    setSearch(event.target.value.trim());
                  }}
                />
                <div className="absolute right-1 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-4 w-4 items-center justify-center flex">
                  {search ? (
                    <img
                      className="cursor-pointer"
                      src={allImgPaths.cross}
                      alt="search"
                      onClick={() => {
                        setFromSearch(true);
                        setSearch("");
                      }}
                    />
                  ) : (
                    <img
                      className="cursor-pointer"
                      src={allImgPaths.search}
                      alt="search"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="m-0 p-0 flex gap-x-2">
              <button
                onClick={addNewEmployerHandler}
                type="button"
                className="bg-[#2E5528] rounded text-white px-4 py-1 items-center gap-x-0 inline-flex justify-center h-10"
                id="menu-button"
                aria-expanded="true"
                aria-haspopup="true"
              >
                <span className="text-sm">Add New Employer</span>
              </button>
              <div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen((prev) => !prev);
                  }}
                  type="button"
                  className={`bg-[#2E5528] rounded text-white px-1 py-1 items-center gap-x-0 inline-flex justify-center h-10`}
                  id="menu-button"
                  aria-expanded="true"
                  aria-haspopup="true"
                >
                  <span className="">
                    <svg
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen((prev) => !prev);
                      }}
                      className="h-5 w-5 text-gray-200"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </button>
                <div
                  className={`${isOpen ? `block` : `hidden`} min-w-32 absolute right-5 z-50 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="menu-button"
                  tabIndex={-1}
                >
                  <div className="py-1" role="none">
                    <span
                      className="text-gray-700 block px-4 py-2 text-sm cursor-pointer hover:bg-[#2E5528] hover:text-white transition-all duration-150"
                      onClick={importEmployerModal}
                    >
                      Import Employer
                    </span>
                  </div>
                  {isExportEmployer > 0 && (
                    <div className="py-1" role="none">
                      <CSVLink
                        filename="Export Employer.csv"
                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-[#2E5528] hover:text-white transition-all duration-150"
                        data={employerData}
                      >
                        Export Employer
                      </CSVLink>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-2">
            <Table
              table={table}
              emptyRecordMsg="No record found"
              isLoading={isLoading}
              loadingMsg="Fetching..."
            />
          </div>
          {/* Pagination Start */}
          <div className="mt-2 pb-4">
            <div className="flex justify-between items-center mt-6">
              {totalData == 0 ? (
                ""
              ) : (
                <div>
                  <span className="mr-2 text-sm">Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    className="border rounded px-2 py-1 text-sm w-[75px]"
                  >
                    {PAGE_LIMIT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {totalData == 0 ? (
                ""
              ) : (
                <div>
                  <h3 className="text-sm">
                    Showing entries {startIndex} to {endIndex} out of{" "}
                    {totalRecords}
                  </h3>
                </div>
              )}
              {totalPageCount > 0 && (
                <ReactPaginate
                  activeClassName="flex items-center group active rounded justify-center h-7 text-white bg-[#2E5528] hover:bg-[#2E5528]"
                  pageLinkClassName="px-3 text-black group-[&.active]:text-white hover:bg-[#2E5528] hover:text-white"
                  pageClassName="cursor-pointer hover:text-white flex items-center rounded justify-center h-7 text-blue-600 text-black hover:bg-[#2E5528] hover:text-white"
                  className="flex gap-5 items-center text-sm"
                  breakLabel="..."
                  nextLabel={currentPage === totalPageCount ? "" : ">"}
                  onPageChange={(event) => {
                    setFromSearch(false);
                    setCurrentPage(+event.selected + 1);
                  }}
                  forcePage={currentPage - 1}
                  pageRangeDisplayed={1}
                  pageCount={totalPageCount}
                  previousLabel={currentPage === 1 ? "" : "<"}
                  renderOnZeroPageCount={null}
                />
              )}
            </div>
          </div>
          {/* Pagination End */}
        </div>
      </Container>
      <Modal
        show={open}
        onClose={() => setOpen(false)}
        dismissible
        size="5xl"
        className="w-full p-0"
      >
        <Modal.Header>
          <div className="text-center">Employer Detail</div>
        </Modal.Header>
        <Modal.Body className="w-full">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <label className="flex items-center gap-1">
              <span className="font-bold">First Name : </span>

              {curUserData && curUserData?.firstName}
            </label>
            <label className="flex items-center gap-1">
              <span className="font-bold">Last Name : </span>

              {curUserData && curUserData?.lastName}
            </label>
            <label className="flex items-center gap-1">
              <span className="font-bold">Email : </span>

              {curUserData && curUserData?.email}
            </label>
            <label className="flex items-center gap-1">
              <span className="font-bold">Gender : </span>

              {curUserData && curUserData?.gender}
            </label>
          </div>
          <hr />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <label>
              <span className="font-bold px-2 py-1 text-sm rounded text-white bg-slate-500">
                Motivator :
              </span>
              <br />
              {curUserData?.motivator && curUserData.motivator.length > 0 ? (
                <div className="flex items-start justify-center">
                  <h5 className="font-bold mt-1 w-[25%] ">Name :</h5>
                  <div className="flex flex-wrap gap-1 w-[75%]">
                    {curUserData.motivator.map((curEle: any, index: number) => (
                      <span
                        key={curEle._id || index}
                        className="py-1 px-3 max-w-max flex items-center rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 hover:bg-opacity-75 focus:bg-gray-300 focus:text-blue-900 active:text-primary active:bg-blue-100"
                      >
                        <p>{curEle.name}</p>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p>No Motivator Detail available.</p>
              )}
            </label>
            <label>
              {curUserData?.company ? (
                <>
                  <span className="font-bold px-2 py-1 text-sm rounded text-white bg-red-500">
                    Company :
                  </span>
                  <p>
                    <b>Diversity :</b> {curUserData.company.companyDiversity}
                  </p>
                  <p>
                    <b>Founded :</b> {curUserData.company.companyFounded}
                  </p>
                  <p>
                    <b>Info :</b> {curUserData.company.companyInfo}
                    {/* <div
                      dangerouslySetInnerHTML={{
                        __html: curUserData.company.companyInfo,
                      }}
                    ></div> */}
                  </p>
                  <p>
                    <b>Name :</b> {curUserData.company.companyName}
                  </p>
                  <p>
                    <b>Score :</b> {curUserData.company.companyScore}
                  </p>
                  <p>
                    <b>Size :</b> {curUserData.company.companySize}
                  </p>
                  <p>
                    <b>Url :</b> {curUserData.company.companyUrl}
                  </p>
                </>
              ) : (
                <p>Company details are not available.</p>
              )}
            </label>
          </div>
        </Modal.Body>
      </Modal>
    </Layout>
  );
};

export default Employer;
